import { NormActualLine, NormActualSaveRow, NormActualSheet, NormHead } from "../../../../api/SiteDaily";

// Draft state and arithmetic for the two outsourced cost heads — Mucking and
// PRW. Pure, no JSX, so the section component stays about rendering.
//
// The clerk types a quantity and a rate; nothing is prefilled from the target.
// A norm rate is what the budget assumed, not what the haulier charged today,
// and prefilling it would quietly turn an assumption into a measurement.

export interface NormDraft {
  // The day's own quantity (not against a civil date)...
  qty: string;
  rate: string;
  remarks: string;
  // ...plus what was entered against civil dates, keyed by work date. Saved
  // as ONE row (qty + Σ pendingDays) with this split beside it; each part is
  // booked on its civil date exactly (as on Material-Stores / Mechanical).
  pendingDays: Record<string, string>;
}

export type TaskNormDrafts = Record<string, NormDraft>;

/** One line's identity within a task-day: a head can have several norms. */
export const normKey = (line: { head: NormHead | string; normId: number }) =>
  `${line.head}:${line.normId}`;

/** Today's saved quantity split back into what it stated per civil date and the rest. */
export const draftOf = (line: NormActualLine): NormDraft => {
  const pendingDays: Record<string, string> = {};
  let onDays = 0;
  line.civilDays.forEach((day) => {
    const v = Number(day.bookedTodayQty);
    if (v > 0) {
      pendingDays[day.workDate] = day.bookedTodayQty;
      onDays += v;
    }
  });
  if (line.actualQty === "" || onDays <= 0) {
    return { qty: line.actualQty, rate: line.actualRate, remarks: line.remarks, pendingDays: {} };
  }
  const rest = Math.round((Number(line.actualQty) - onDays) * 10000) / 10000;
  return { qty: rest > 0.00005 ? String(rest) : "", rate: line.actualRate, remarks: line.remarks, pendingDays };
};

export const draftsOf = (sheet: NormActualSheet | null): TaskNormDrafts => {
  const drafts: TaskNormDrafts = {};
  (sheet?.lines ?? []).forEach((line) => {
    drafts[normKey(line)] = draftOf(line);
  });
  return drafts;
};

const n = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const has = (value: string) => value !== undefined && value !== null && value !== "";
const r2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

/** The line's whole quantity for the day: its own qty + Σ civil-date parts. */
export const totalQtyOf = (draft?: NormDraft): string => {
  if (!draft) return "";
  const days = Object.values(draft.pendingDays ?? {}).reduce((sum, v) => sum + (has(v) ? n(v) : 0), 0);
  if (days <= 0) return draft.qty;
  return String(Math.round(((has(draft.qty) ? n(draft.qty) : 0) + days) * 10000) / 10000);
};

/** '' when either side is unstated — an unrated quantity has no amount, not 0. */
export const amountOf = (draft?: NormDraft): string => {
  const qty = totalQtyOf(draft);
  if (!draft || !has(qty) || !has(draft.rate)) return "";
  return String(r2(n(qty) * n(draft.rate)));
};

/** Variance on amount against the target. '' when there is nothing to compare. */
export const varianceOf = (line: NormActualLine, draft?: NormDraft): string => {
  const actual = amountOf(draft);
  if (actual === "" || !has(line.targetAmount) || n(line.targetAmount) <= 0) return "";
  return String(r2(((n(actual) - n(line.targetAmount)) / n(line.targetAmount)) * 100));
};

/** The rate typed today is above the rate the target was costed at. */
export const aboveNorm = (line: NormActualLine, draft?: NormDraft): boolean =>
  !!draft && has(draft.rate) && has(line.targetRate) && n(draft.rate) - n(line.targetRate) > 0.01;

const sameDays = (a: Record<string, string>, b: Record<string, string>) => {
  const filled = (days: Record<string, string>) =>
    Object.entries(days ?? {})
      .filter(([, v]) => v !== "")
      .sort(([x], [y]) => x.localeCompare(y))
      .join("|");
  return filled(a) === filled(b);
};

export const isDirty = (line: NormActualLine, draft?: NormDraft): boolean => {
  if (!draft) return false;
  const saved = draftOf(line);
  return (
    draft.qty !== saved.qty ||
    !sameDays(draft.pendingDays, saved.pendingDays) ||
    draft.rate !== line.actualRate ||
    (draft.remarks || "") !== (line.remarks || "")
  );
};

export const anyDirty = (sheet: NormActualSheet | null, drafts: TaskNormDrafts): boolean =>
  (sheet?.lines ?? []).some((line) => isDirty(line, drafts[normKey(line)]));

/**
 * The save payload: a FULL replace-set for the task-day. A head with no
 * quantity is left out, which is how "nothing was outsourced today" is stated —
 * sending a zero would be a different claim.
 */
export const saveRowsOf = (sheet: NormActualSheet | null, drafts: TaskNormDrafts): NormActualSaveRow[] =>
  (sheet?.lines ?? [])
    .map((line) => ({ line, draft: drafts[normKey(line)] }))
    .filter(({ draft }) => draft && has(totalQtyOf(draft)) && n(totalQtyOf(draft)) > 0)
    .map(({ line, draft }) => ({
      head: line.head,
      normId: line.normId,
      // ONE row: the day's own qty + the civil-date parts, which go up beside
      // it and are booked on their dates exactly.
      qty: totalQtyOf(draft),
      civilDates: Object.entries(draft.pendingDays ?? {})
        .filter(([, qty]) => has(qty) && n(qty) > 0)
        .map(([civilDate, qty]) => ({ civilDate, qty })),
      ...(has(draft.rate) ? { rate: draft.rate } : {}),
      ...(draft.remarks ? { remarks: draft.remarks } : {}),
    }));

export const HEAD_LABEL: Record<NormHead, string> = {
  MUCKING: "Mucking",
  PRW: "PRW",
};

/** Day totals across both heads, for the task header. */
export const totalsOf = (sheet: NormActualSheet | null, drafts: TaskNormDrafts) => {
  let target = 0;
  let actual = 0;
  (sheet?.lines ?? []).forEach((line) => {
    if (has(line.targetAmount)) target += n(line.targetAmount);
    const amount = amountOf(drafts[normKey(line)]);
    if (amount !== "") actual += n(amount);
  });
  return {
    targetAmount: target ? String(r2(target)) : "",
    actualAmount: actual ? String(r2(actual)) : "",
    variancePercent: target > 0 && actual ? String(r2(((actual - target) / target) * 100)) : "",
  };
};
